import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, TrendingUp, Users, Headphones, ShoppingCart, ArrowRight } from 'lucide-react';

const Services = () => {
  const services = [
    {
      title: 'Graphic Design & Branding',
      description:
        'Professional, modern, and strategic visual design for businesses that want to stand out.',
      includes: ['Logos', 'Brand kits', 'Social media graphics', 'Marketing materials', 'Product packaging'],
      icon: <Sparkles className="w-8 h-8" />,
      image: 'https://images.pexels.com/photos/16307279/pexels-photo-16307279.jpeg',
      color: 'cyan',
    },
    {
      title: 'Business Consultancy & Strategy',
      description:
        'Insight-driven guidance to help businesses operate smarter and grow faster.',
      includes: [
        'Process optimization',
        'Workflow design',
        'Market positioning',
        'Brand strategy',
        'Digital transformation support',
      ],
      icon: <TrendingUp className="w-8 h-8" />,
      image: 'https://images.pexels.com/photos/7654185/pexels-photo-7654185.jpeg',
      color: 'orange',
    },
    {
      title: 'Digital Content & Social Media Support',
      description: 'Content that aligns with your brand and drives engagement.',
      includes: [
        'Social media posts',
        'Content planning',
        'Caption writing',
        'Page optimization',
        'Faceless brand content',
      ],
      icon: <Users className="w-8 h-8" />,
      image: 'https://images.unsplash.com/photo-1724862936518-ae7fcfc052c1',
      color: 'cyan',
    },
    {
      title: 'Administrative & Virtual Support',
      description: 'Dependable support for busy entrepreneurs and teams.',
      includes: [
        'Data entry',
        'Research',
        'Documentation',
        'Email & file organization',
        'Reporting and dashboards',
      ],
      icon: <Headphones className="w-8 h-8" />,
      image: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5',
      color: 'orange',
    },
    {
      title: 'E-commerce Assistance',
      description: 'Helping businesses build, manage, and scale their online stores.',
      includes: [
        'Product listing',
        'Store setup',
        'Optimization',
        'Customer support templates',
        'Marketplace compliance',
      ],
      icon: <ShoppingCart className="w-8 h-8" />,
      image: 'https://images.pexels.com/photos/577210/pexels-photo-577210.jpeg',
      color: 'cyan',
    },
  ];

  return (
    <div className="bg-eclipse-bg">
      {/* Hero Section */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at top right, rgba(0, 217, 255, 0.15), transparent 40%), radial-gradient(circle at bottom left, rgba(255, 140, 66, 0.1), transparent 40%)',
        }}
        data-testid="services-hero-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan mb-4">
              Our Services
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none mb-6">
              Comprehensive{' '}
              <span className="bg-gradient-to-r from-brand-cyan to-brand-orange bg-clip-text text-transparent">
                Digital Solutions
              </span>
            </h1>
            <p className="text-lg lg:text-xl text-eclipse-secondary leading-relaxed">
              From brand identity to business strategy, we offer end-to-end services designed to help
              your business thrive.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 md:py-32" data-testid="services-list-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 space-y-24">
          {services.map((service, index) => (
            <div
              key={index}
              className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
              data-testid={`service-detail-${index + 1}`}
            >
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div
                  className={`inline-flex p-3 rounded-full mb-6 ${
                    service.color === 'cyan' ? 'bg-brand-cyan/10' : 'bg-brand-orange/10'
                  }`}
                >
                  <div className={service.color === 'cyan' ? 'text-brand-cyan' : 'text-brand-orange'}>
                    {service.icon}
                  </div>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{service.title}</h2>
                <p className="text-eclipse-secondary leading-relaxed mb-6">{service.description}</p>
                <div className="space-y-3">
                  <p className="text-eclipse-text font-semibold">Includes:</p>
                  <ul className="space-y-2">
                    {service.includes.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-eclipse-secondary">
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${
                            service.color === 'cyan' ? 'bg-brand-cyan' : 'bg-brand-orange'
                          }`}
                        ></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                <div className="relative group">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-2xl shadow-2xl w-full h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div
                    className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                      service.color === 'cyan'
                        ? 'bg-brand-cyan/10'
                        : 'bg-brand-orange/10'
                    }`}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at center, rgba(0, 217, 255, 0.1), transparent 50%)',
        }}
        data-testid="services-cta-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-eclipse-secondary text-lg leading-relaxed mb-8">
            Let's discuss how our services can help you achieve your goals. Get in touch with our team
            today.
          </p>
          <Link
            to="/contact"
            data-testid="services-contact-cta"
            className="inline-flex items-center gap-2 bg-brand-cyan text-eclipse-bg font-bold px-10 py-5 rounded-full hover:bg-white hover:scale-105 transition-all duration-300 text-lg"
          >
            Start Your Project
            <ArrowRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;